import { useSelector } from 'react-redux';

/**
 * The screen's `screenVariable` as returned by /getScreensAccess. This is the
 * contract between the two access slices: `screensAccess.data[KEY]` carries the
 * screen's id + access flag, and the features for that screen are filed under
 * `featuresAccess[KEY]`. Declared once here so the string isn't repeated across
 * call sites — and so the numeric screen id is never written down at all (it
 * comes from the API response, see useScreenAccess below).
 */
export const PD_SCREEN_VARIABLE = 'ProductdevelopmentDashboard';

/**
 * Screen-level access for the Product Development dashboard:
 * `{ screenName, screenVariable, screenId, screenAccess }`, straight from
 * /getScreensAccess. Use `screenId` to fetch features rather than a literal —
 * the same pattern every other dashboard uses (e.g. inactivePODashboard).
 */
export const useScreenAccess = () =>
  useSelector(
    (state) =>
      state?.pcmp?.common?.accessControl?.screensAccess?.data?.[PD_SCREEN_VARIABLE]
  );

/**
 * Has /getScreensAccess resolved yet?
 *
 * Needed because useScreenAccess() returns `undefined` in BOTH cases — while the
 * call is still in flight, and when the role simply has no entry for this screen.
 * A redirect guard must not confuse the two, or it would bounce every user out
 * mid-load before their access is even known.
 */
export const useScreensAccessLoaded = () =>
  useSelector((state) =>
    Boolean(state?.pcmp?.common?.accessControl?.screensAccess?.data)
  );

/**
 * Product Development dashboard feature access, exactly as the API returns it.
 *
 * Every role — including Admin — is gated by the same `featuresAccess` object.
 * There is deliberately NO admin bypass: what the backend grants is what shows.
 * (The previous version returned a Proxy for `localStorage.roleName === 'Admin'`
 * where every key resolved to `true`, which meant admins could never see — or
 * test — the real gating.)
 *
 * Returns `undefined` until the features call resolves, so callers must treat a
 * missing value as "not granted yet" (`featuresAccess?.someFlag`).
 *
 * The value comes straight from the store, so it is referentially stable across
 * renders and safe to use in effect dependency arrays.
 */
export const useFeaturesAccess = () =>
  useSelector(
    (state) =>
      state?.pcmp?.common?.accessControl?.featuresAccess?.[PD_SCREEN_VARIABLE]
  );

export default useFeaturesAccess;
